var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SpaAuth } from '@resmed/identity-spa-auth-js';
import { useAuth, useAuthEvents } from '@resmed/identity-spa-auth-react';
import { SessionTimeoutDialog } from 'components/common/SessionTimeoutDialog';
import { logger } from 'utils/logger';
const WARNING_BEFORE_EXPIRY_MS = 60000; // 1 minute
export const SessionManager = () => {
    const { isAuthenticated, expiresAt, login, logout, refresh } = useAuth();
    const { subscribeLifecycle } = useAuthEvents();
    const [open, setOpen] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [isRefreshInProgress, setIsRefreshInProgress] = useState(false);
    const [isLogoutInProgress, setIsLogoutInProgress] = useState(false);
    // Dummy counter used to trigger re-scheduling of expiry when other tabs
    // successfully refresh the session.
    const [scheduleTick, setScheduleTick] = useState(0);
    const warningTimeoutId = useRef(null);
    const countdownIntervalId = useRef(null);
    const logoutJitterTimeoutId = useRef(null);
    const logoutInProgressRef = useRef(false);
    const uiBroadcastChannelRef = useRef(null);
    const logoutRedirectTriggeredRef = useRef(false);
    const clearTimers = useCallback(() => {
        if (warningTimeoutId.current !== null) {
            globalThis.clearTimeout(warningTimeoutId.current);
            warningTimeoutId.current = null;
        }
        if (countdownIntervalId.current !== null) {
            globalThis.clearInterval(countdownIntervalId.current);
            countdownIntervalId.current = null;
        }
        if (logoutJitterTimeoutId.current !== null) {
            globalThis.clearTimeout(logoutJitterTimeoutId.current);
            logoutJitterTimeoutId.current = null;
        }
    }, []);
    const performLogoutWithFallback = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            logger.info('SessionManager: starting logout with redirect to IdP');
            yield logout({ redirectToLogout: true });
        }
        catch (error) {
            logger.warn('Logout failed (likely timing issue), retrying login', { error });
            // Any error (400 or otherwise): wait 10s, then start a fresh login
            // flow. This gives the IdP logout more time to complete and keeps
            // the behavior consistent between initiating and follower tabs.
            yield new Promise((resolve) => {
                globalThis.setTimeout(resolve, 10000);
            });
            try {
                yield login({ redirectToLogin: true });
            }
            catch (_a) {
                // Ignore login redirect errors here; user remains on a logged-out page.
            }
        }
    }), [login, logout]);
    const scheduleLogoutWithJitter = useCallback(() => {
        const jitterMs = Math.floor(Math.random() * 5000);
        logoutJitterTimeoutId.current = globalThis.setTimeout(() => {
            // Clear our own handle so subsequent clearTimers() calls don't try to
            // cancel a timeout that has already fired.
            logoutJitterTimeoutId.current = null;
            performLogoutWithFallback().catch((error) => {
                logger.warn('Logout fallback flow encountered an unexpected error', { error });
            });
        }, jitterMs);
        logger.info('SessionManager: scheduled logout with jitter', { jitterMs });
    }, [performLogoutWithFallback]);
    // Lightweight UI-level BroadcastChannel so that session timeout modals
    // coordinate across tabs (e.g. "stay signed in" in one tab affects
    // others immediately), independent of the SDK's internal channel.
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }
        const channel = new BroadcastChannel('session-timeout-ui');
        uiBroadcastChannelRef.current = channel;
        channel.onmessage = (event) => {
            const message = event.data;
            switch (message.type) {
                case 'refresh-started':
                    setIsRefreshInProgress(true);
                    break;
                case 'refresh-finished':
                    setIsRefreshInProgress(false);
                    // Any successful refresh from any tab cancels the current
                    // timeout flow in this tab. Bump scheduleTick so the expiry
                    // effect re-runs using the updated expiry from auth context.
                    clearTimers();
                    setOpen(false);
                    setScheduleTick((tick) => tick + 1);
                    // A successful refresh cancels any pending logout flow in this tab.
                    logoutInProgressRef.current = false;
                    setIsLogoutInProgress(false);
                    logoutRedirectTriggeredRef.current = false;
                    break;
                case 'logout-started':
                    // Any logout event (initiated locally or via another tab) cancels
                    // any in-flight refresh UI state in this tab.
                    setIsRefreshInProgress(false);
                    logoutInProgressRef.current = true;
                    setIsLogoutInProgress(true);
                    // Once logout begins in any tab, cancel local timers.
                    clearTimers();
                    setOpen(false);
                    break;
            }
        };
        return () => {
            channel.close();
            uiBroadcastChannelRef.current = null;
        };
    }, [clearTimers]);
    // Subscribe to SDK lifecycle events (e.g. for same-tab progress state and
    // cross-tab logout coordination).
    useEffect(() => {
        const unsubscribe = subscribeLifecycle((event) => {
            if (event.type === 'refresh') {
                if (event.phase === 'started') {
                    setIsRefreshInProgress(true);
                }
                else {
                    setIsRefreshInProgress(false);
                }
            }
            else if (event.type === 'logout') {
                if (event.phase === 'started') {
                    logoutInProgressRef.current = true;
                    setIsLogoutInProgress(true);
                    // Mark this tab so the succeeded handler below doesn't call logout
                    // a second time. logout:started fires only on the tab that initiated
                    // the logout (e.g. via HeaderMenu), not on follower tabs.
                    logoutRedirectTriggeredRef.current = true;
                    clearTimers();
                    setOpen(false);
                }
                else if (event.phase === 'succeeded') {
                    // Any tab that observes a logout success will call logout via the
                    // helper. This narrows the window to just after the OAuth Agent
                    // has completed /logout, before the IdP redirect has fully
                    // propagated. On any error, the helper waits 10s, then calls
                    // /login/start.
                    if (!logoutRedirectTriggeredRef.current) {
                        logoutRedirectTriggeredRef.current = true;
                        clearTimers();
                        setOpen(false);
                        performLogoutWithFallback().catch((error) => {
                            logger.warn('Logout fallback flow encountered an unexpected error', { error });
                        });
                    }
                }
            }
        });
        return () => {
            unsubscribe();
        };
    }, [clearTimers, performLogoutWithFallback, subscribeLifecycle]);
    useEffect(() => {
        clearTimers();
        setOpen(false);
        if (!isAuthenticated || !expiresAt) {
            return;
        }
        const now = Date.now();
        const expiryTime = new Date(expiresAt).getTime();
        if (Number.isNaN(expiryTime)) {
            return;
        }
        const msToExpiry = expiryTime - now;
        if (msToExpiry <= 0) {
            return;
        }
        const startCountdown = () => {
            var _a;
            setOpen(true);
            logger.info('SessionManager: session timeout dialog opened');
            const updateRemainingAndMaybeLogout = () => {
                var _a;
                // Prefer the SDK's view of expiry, but fall back to the last known
                // expiresAt value from the auth context so we don't log out early
                // if SpaAuth is temporarily unavailable.
                let shouldLogout = false;
                let secondsRemaining;
                let msToExpiryNow = null;
                try {
                    const auth = SpaAuth.get();
                    const expiresDate = auth.getExpiresAt();
                    if (expiresDate) {
                        msToExpiryNow = expiresDate.getTime() - Date.now();
                    }
                }
                catch (_b) {
                    // Ignore and fall back to the captured expiryTime below.
                }
                msToExpiryNow !== null && msToExpiryNow !== void 0 ? msToExpiryNow : (msToExpiryNow = expiryTime - Date.now());
                if (!Number.isFinite(msToExpiryNow) || msToExpiryNow <= 0) {
                    shouldLogout = true;
                    secondsRemaining = 0;
                }
                else {
                    secondsRemaining = Math.max(0, Math.ceil(msToExpiryNow / 1000));
                }
                setRemainingSeconds(secondsRemaining);
                if (secondsRemaining <= 0) {
                    if (countdownIntervalId.current !== null) {
                        globalThis.clearInterval(countdownIntervalId.current);
                        countdownIntervalId.current = null;
                    }
                    if (shouldLogout && !logoutRedirectTriggeredRef.current) {
                        (_a = uiBroadcastChannelRef.current) === null || _a === void 0 ? void 0 : _a.postMessage({ type: 'logout-started' });
                        // Mark this tab as having initiated a logout so we don't
                        // re-handle the subsequent auth event.
                        logoutRedirectTriggeredRef.current = true;
                        logoutInProgressRef.current = true;
                        setIsLogoutInProgress(true);
                        scheduleLogoutWithJitter();
                    }
                }
            };
            // Initialize immediately so we show an accurate value from the start.
            updateRemainingAndMaybeLogout();
            (_a = countdownIntervalId.current) !== null && _a !== void 0 ? _a : (countdownIntervalId.current = globalThis.setInterval(updateRemainingAndMaybeLogout, 1000));
        };
        const msUntilWarning = msToExpiry - WARNING_BEFORE_EXPIRY_MS;
        if (msUntilWarning <= 0) {
            startCountdown();
        }
        else {
            warningTimeoutId.current = globalThis.setTimeout(startCountdown, msUntilWarning);
        }
        return clearTimers;
    }, [clearTimers, isAuthenticated, expiresAt, scheduleTick, scheduleLogoutWithJitter]);
    const handleSignOutNow = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (logoutInProgressRef.current) {
            return;
        }
        logger.info('SessionManager: user chose to sign out now');
        clearTimers();
        setOpen(false);
        // Local guard; SDK lifecycle will also mark logout started for all tabs
        logoutInProgressRef.current = true;
        setIsLogoutInProgress(true);
        (_a = uiBroadcastChannelRef.current) === null || _a === void 0 ? void 0 : _a.postMessage({ type: 'logout-started' });
        // Ensure this tab does not re-handle the auth logout event.
        logoutRedirectTriggeredRef.current = true;
        yield performLogoutWithFallback();
    });
    const handleStaySignedIn = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (isRefreshInProgress) {
            return;
        }
        logger.info('SessionManager: user chose to stay signed in');
        clearTimers();
        setOpen(false);
        // Local hint; SDK lifecycle will broadcast refresh-started across tabs
        setIsRefreshInProgress(true);
        try {
            (_a = uiBroadcastChannelRef.current) === null || _a === void 0 ? void 0 : _a.postMessage({ type: 'refresh-started' });
            yield refresh();
            (_b = uiBroadcastChannelRef.current) === null || _b === void 0 ? void 0 : _b.postMessage({ type: 'refresh-finished' });
        }
        catch (error) {
            logger.warn('Token refresh failed; starting logout fallback flow', { error });
            logoutInProgressRef.current = true;
            setIsLogoutInProgress(true);
            (_c = uiBroadcastChannelRef.current) === null || _c === void 0 ? void 0 : _c.postMessage({ type: 'logout-started' });
            logoutRedirectTriggeredRef.current = true;
            yield performLogoutWithFallback();
        }
        finally {
            setIsRefreshInProgress(false);
        }
    });
    if (!open) {
        return null;
    }
    return (_jsx(SessionTimeoutDialog, { open: open, remainingSeconds: remainingSeconds, onSignOut: handleSignOutNow, onStaySignedIn: handleStaySignedIn, disableStaySignedIn: isRefreshInProgress, disableSignOut: isLogoutInProgress }));
};
